import { Injectable } from '@angular/core';
import type { Category } from '../types/category.types';
import categorias from '../data-preloaded/grupos-data';
@Injectable({
  providedIn: 'root',
})
export class CategoriesService {
  private allCategories: Category[] = categorias?.grupos || [];
  private populares: Category[] = categorias?.populares || [];

  /**
   * Obtiene todas las categorías principales
   */
  getAllCategories(): Category[] {
    return this.allCategories;
  }

  /**
   * Busca una categoría por slug
   */
  findCategoryBySlug(slug: string): Category | null {
    return this.findCategoryInTree(this.allCategories, slug);
  }

  /**
   * Busca una categoría en el árbol recursivamente
   */
  private findCategoryInTree(
    categories: Category[],
    slug: string
  ): Category | null {
    for (const category of categories) {
      if (category.slug === slug) {
        return category;
      }
      if (category.children) {
        const found = this.findCategoryInTree(category.children, slug);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Obtiene el path completo de breadcrumbs para una categoría
   */
  getCategoryPath(slug: string): Category[] {
    const path: Category[] = [];
    this.findPath(this.allCategories, slug, path);
    return path;
  }

  /**
   * Encuentra el path recursivamente
   */
  private findPath(
    categories: Category[],
    targetSlug: string,
    currentPath: Category[]
  ): boolean {
    for (const category of categories) {
      currentPath.push(category);

      if (category.slug === targetSlug) {
        return true;
      }

      if (
        category.children &&
        this.findPath(category.children, targetSlug, currentPath)
      ) {
        return true;
      }

      currentPath.pop();
    }
    return false;
  }

  /**
   * Obtiene las partes populares para una categoría
   */
  getPopularParts(categorySlug: string): Category[] {
    const category = this.findCategoryBySlug(categorySlug);
    return category?.populars || [];
  }

  /**
   * Obtiene las subcategorías de una categoría
   */
  getSubcategories(categorySlug: string): Category[] {
    const category = this.findCategoryBySlug(categorySlug);
    return category?.children || [];
  }

  /**
   * Obtiene la categoria y todos sus padres
   */
  getCategoryAndParents(categorySlug: string): Category[] {
    return this.getCategoryPath(categorySlug);
  }

  /**
   * Cuenta el número total de subcategorías (recursivo)
   */
  countSubcategories(category: Category): number {
    if (!category.children) return 0;

    let count = category.children.length;
    category.children.forEach((child) => {
      count += this.countSubcategories(child);
    });

    return count;
  }

  /**
   * Obtiene las categorías principales recomendadas
   */
  getMainCategories(): Category[] {
    return [...(categorias?.populares || [])];
  }

  /**
   * Busca categorías por texto de forma inteligente
   */
  searchCategories(query: string): Category[] {
    const results: (Category & { relevanceScore: number })[] = [];
    const searchTerms = this.parseSearchTerms(query);

    if (searchTerms.length === 0) {
      return [];
    }

    this.searchInCategoriesIntelligent(
      this.allCategories,
      searchTerms,
      results
    );

    // Ordenar resultados por relevancia
    return this.sortByRelevance(results, searchTerms);
  }

  /**
   * Parsea los términos de búsqueda separando palabras y limpiando
   */
  private parseSearchTerms(query: string): string[] {
    // Palabras conectoras que se deben omitir
    const stopWords = new Set([
      'de',
      'del',
      'la',
      'las',
      'el',
      'los',
      'y',
      'o',
      'con',
      'para',
      'por',
      'en',
      'a',
      'al',
      'un',
      'una',
      'unos',
      'unas',
      'que',
      'cual',
      'cuales',
      'donde',
      'cuando',
      'como',
      'porque',
      'si',
      'no',
      'ni',
      'pero',
      'mas',
      'sin',
      'sobre',
      'entre',
      'hacia',
      'hasta',
      'desde',
      'tras',
      'durante',
      'mediante',
      'según',
      'contra',
      'bajo',
      'ante',
      'bajo',
    ]);

    return query
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter((term) => term.length >= 2) // Solo términos de 2+ caracteres
      .map((term) => this.normalizeText(term)) // Normalizar texto (quitar tildes)
      .filter((term) => term.length > 0 && !stopWords.has(term)); // Filtrar palabras conectoras
  }

  /**
   * Normaliza texto removiendo tildes y caracteres especiales
   */
  private normalizeText(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos (tildes)
      .replace(/[^\w]/g, '') // Solo letras y números
      .toLowerCase();
  }

  /**
   * Busca en categorías de forma inteligente con múltiples términos
   */
  private searchInCategoriesIntelligent(
    categories: Category[],
    searchTerms: string[],
    results: (Category & { relevanceScore: number })[]
  ): void {
    for (const category of categories) {
      const categoryName = this.normalizeText(category.name);
      const categorySlug = this.normalizeText(category.slug);

      // Calcular score de relevancia para esta categoría
      const relevanceScore = this.calculateRelevanceScore(
        categoryName,
        categorySlug,
        searchTerms
      );

      if (relevanceScore > 0) {
        // Verificar que no esté duplicado
        const existingIndex = results.findIndex(
          (r) => r.slug === category.slug
        );
        if (existingIndex === -1) {
          // Agregar score a la categoría para ordenamiento posterior
          const categoryWithScore = { ...category, relevanceScore };
          results.push(
            categoryWithScore as Category & { relevanceScore: number }
          );
        } else {
          // Si ya existe, actualizar el score si es mayor
          if (relevanceScore > results[existingIndex].relevanceScore) {
            results[existingIndex].relevanceScore = relevanceScore;
          }
        }
      }

      // Buscar recursivamente en subcategorías
      if (category.children) {
        this.searchInCategoriesIntelligent(
          category.children,
          searchTerms,
          results
        );
      }
    }
  }

  /**
   * Calcula el score de relevancia para una categoría
   */
  private calculateRelevanceScore(
    categoryName: string,
    categorySlug: string,
    searchTerms: string[]
  ): number {
    let score = 0;
    let matchedTerms = 0;

    for (const term of searchTerms) {
      let termMatched = false;

      // Buscar en el nombre de la categoría
      if (categoryName.includes(term)) {
        score += 10; // Peso alto para coincidencia exacta en nombre
        termMatched = true;

        // Bonus si es el primer término
        if (categoryName.startsWith(term)) {
          score += 5;
        }
      }

      // Buscar en el slug
      if (categorySlug.includes(term)) {
        score += 8;
        termMatched = true;
      }

      // Buscar palabras completas
      const words = categoryName.split(/\s+/);
      for (const word of words) {
        if (word === term) {
          score += 15; // Máximo peso para palabra exacta
          termMatched = true;
        } else if (word.startsWith(term)) {
          score += 8; // Bonus para palabras que empiezan con el término
          termMatched = true;
        }
      }

      if (termMatched) {
        matchedTerms++;
      }
    }

    // Lógica AND: solo devolver score si TODOS los términos coinciden
    if (matchedTerms === searchTerms.length && searchTerms.length > 0) {
      // Bonus por coincidencia completa
      score += searchTerms.length * 5;
      return score;
    }

    return 0; // No hay coincidencia completa
  }

  /**
   * Ordena los resultados por relevancia
   */
  private sortByRelevance(
    results: (Category & { relevanceScore: number })[],
    searchTerms: string[]
  ): Category[] {
    return results
      .sort((a, b) => {
        // Primero por score de relevancia
        if (b.relevanceScore !== a.relevanceScore) {
          return b.relevanceScore - a.relevanceScore;
        }

        // Si tienen el mismo score, ordenar por longitud del nombre (más específico primero)
        return a.name.length - b.name.length;
      })
      .map(({ relevanceScore, ...category }) => category); // Remover relevanceScore del resultado final
  }

  /**
   * Verifica si una categoría es hoja (no tiene subcategorías)
   */
  isLeafCategory(category: Category): boolean {
    return !category.children || category.children.length === 0;
  }

  /**
   * Obtiene categorías hermanas (mismo nivel)
   */
  getSiblingCategories(categorySlug: string): Category[] {
    const path = this.getCategoryPath(categorySlug);
    if (path.length < 2) {
      // Es una categoría principal, devolver todas las principales
      return this.allCategories.filter((cat) => cat.slug !== categorySlug);
    }

    const parentCategory = path[path.length - 2];
    return (
      parentCategory.children?.filter((cat) => cat.slug !== categorySlug) || []
    );
  }

  /**
   * Obtiene la categoría padre de una categoría dada
   */
  getParentCategory(categorySlug: string): Category | null {
    const path = this.getCategoryPath(categorySlug);
    if (path.length < 2) {
      // Es una categoría principal, no tiene padre
      return null;
    }

    // El padre es el penúltimo elemento en el path
    return path[path.length - 2];
  }
}
